import React, { FocusEvent, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Checkbox, CheckboxProps, Typography } from 'antd';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';

import { InputNumberForm } from '@/components/form/InputNumber';
import { RadioGroupForm } from '@/components/form/Radio';
import { useAppDispatch, useAppSelector } from '@/core/redux/hooks';
import { systemCalculationsActions } from '@/core/redux/slices/smokeExtraction/system/calculations/slice';
import { systemSelectors } from '@/core/redux/slices/smokeExtraction/system/selectors';

import { DynamicAerodynamicsTable } from './DynamicAerodynamicsTable/DynamicAerodynamicsTable';
import { AdditionalStaticPressureTable } from './AdditionalStaticPressure';
import { GravTable } from './GravTable';

const { Text, Title } = Typography;

interface IAerodynamicsView {
  systemID: number;
  isObserver: boolean;
}

const compareArrays = (array1: any, array2: any) => {
  if (!array1 || array2!) return false;
  array1.forEach((elem: any, index: number) => {
    compareObjects(elem, array2[index]);
  });
};

const compareObjects = (obj1: any, obj2: any) => {
  for (const key in obj1) {
    if (!Array.isArray(obj1[key]) && obj1[key] != obj2[key]) {
      return false;
    } else {
      compareArrays(obj1[key], obj2[key]);
    }
  }
  return true;
};

export const AerodynamicsView: React.FC<IAerodynamicsView> = ({
  systemID,
  isObserver,
}) => {
  const systemState = useAppSelector(systemSelectors.systemState);

  const dispatch = useAppDispatch();

  const methods = useForm({
    defaultValues: {
      G: null,
      t: null,
      rho: null,
      Lv: null,
      Pst: null,
      tgrav1: null,
      tgrav2: null,
      hgrav: null,
      Pgrav: null,
      Pst_apo: null,
      Ps: null,
      reserve: null,
      Ps_reserve: null,
      Pizb_enabled: false,
      Pizb: null,
      additional_static_pressure_enabled: false,
      sumP0: null,
      mode: 'кг/с',
      v_threshold: '20',
      P_kon_uch_threshold: '1000',
      grav_table: [
        { i: 1, tgrav1: null, tgrav2: null, hgrav: null, Pgravi: null },
      ],
      additional_static_pressure_table: [
        {
          i: 1,
          Dekv: null,
          E: null,
          G0: null,
          Ke: null,
          L0: null,
          P0: null,
          Pm: null,
          Ptr: null,
          R: null,
          Re: null,
          S: null,
          a0: null,
          b0: null,
          l: null,
          lmbda: null,
          p0: null,
          t0: null,
          v: null,
          v0: null,
        },
      ],
      result_table: [
        {
          i: 1,
          G_nach_uch: null,
          t_nach_uch: null,
          T_nach_uch: null,
          t1: null,
          t2: null,
          rho1: null,
          rho2: null,
          cp1: null,
          cp2: null,
          nu1: null,
          nu2: null,
          rho: null,
          Cp_na_uch: null,
          nu: null,
          Ke: null,
          A: null,
          B: null,
          S: null,
          Dek: null,
          v: null,
          Pdin: null,
          Re: null,
          lmbda: null,
          R: null,
          l: null,
          Rl: null,
          Xi_sum: null,
          z: null,
          P_uch_sum: null,
          P_kon_uch: null,
          Gdop: null,
          Gsum: null,
          suction_leakage_type: 'Учитывать',
          delta_P: null,
          Akl: null,
          Bkl: null,
          Fkl: null,
          Sd20: null,
          Sd: null,
          delta_Gkl: null,
          tightness_class: null,
          Fvozd: null,
          Ffas: null,
          Fvozd_sum: null,
          rho_utpod: null,
          A_tc: null,
          B_tc: null,
          C_tc: null,
          D_tc: null,
          delta_Gvozd: null,
          delta_Gpd_sum: null,
          G_kon_uch: null,
          ql: 0,
          t_pods: null,
          T_pods: null,
          t1_pods: null,
          t2_pods: null,
          cp1_pods: null,
          cp2_pods: null,
          rho1_pods: null,
          rho2_pods: null,
          rho_pods: null,
          cp_pods: null,
          T_kon_uch: null,
          t_kon_uch: null,
          cp_kon_uch: null,
          rho_kon_uch: null,
          t1_kon_uch: null,
          t2_kon_uch: null,
          cp1_kon_uch: null,
          cp2_kon_uch: null,
          rho1_kon_uch: null,
          rho2_kon_uch: null,
        },
      ],
    },
  });

  const coverTypeOptions = [
    { label: 'кг/с', value: 'кг/с' },
    { label: 'м³/ч', value: 'м3/ч' },
  ];

  const handlePerformCalculation = () => {
    const data = methods.getValues();

    const cloneData = cloneDeep(data);

    if (cloneData?.grav_table?.[0]?.['i'] === undefined) {
      cloneData.grav_table[0]['i'] = 1;
    }

    dispatch(
      systemCalculationsActions.performAerodynamics({
        calc_id: systemID,
        data: cloneData,
      })
    );
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (!systemState || systemState[name] !== value) {
      handlePerformCalculation();
    }
  };

  const blurFunction = debounce(handleBlur, 1000);

  useEffect(() => {
    const test = methods.getValues();
    systemState &&
      !compareObjects(systemState, test) &&
      methods.reset(systemState);
  }, [systemState]);

  const Pizb_enabled = systemState?.Pizb_enabled;
  const additional_static_pressure_enabled =
    systemState?.additional_static_pressure_enabled;

  const handlePizbCheckBox: CheckboxProps['onChange'] = (e) => {
    methods.setValue('Pizb_enabled', e?.target?.checked);
    if (e?.target?.checked && additional_static_pressure_enabled) {
      methods.setValue('additional_static_pressure_enabled', false);
      methods.setValue('sumP0', null);
    }
    !e?.target?.checked && methods.setValue('Pizb', null);
    handlePerformCalculation();
  };

  const handleAdditionalStaticPressureCheckBox: CheckboxProps['onChange'] = (
    e
  ) => {
    methods.setValue('additional_static_pressure_enabled', e?.target?.checked);
    if (e?.target?.checked && Pizb_enabled) {
      methods.setValue('Pizb_enabled', false);
      methods.setValue('Pizb', null);
    }
    if (!e?.target?.checked) {
      methods.setValue('sumP0', null);
      methods.setValue('additional_static_pressure_table', []);
    } else {
      methods.setValue('additional_static_pressure_table', [
        {
          i: 1,
          Dekv: null,
          E: null,
          G0: null,
          Ke: null,
          L0: null,
          P0: null,
          Pm: null,
          Ptr: null,
          R: null,
          Re: null,
          S: null,
          a0: null,
          b0: null,
          l: null,
          lmbda: null,
          p0: null,
          t0: null,
          v: null,
          v0: null,
        },
      ]);
    }

    handlePerformCalculation();
  };

  return (
    <FormProvider {...methods}>
      <form className='flex flex-col gap-8'>
        <Title level={5}>Данные для подбора вентилятора</Title>
        <div className='grid grid-cols-2 gap-4 gap-y-8 items-end'>
          <InputNumberForm
            name='G'
            addonBefore={<i>G =</i>}
            addonAfter={<i>кг/с</i>}
            label='Массовый расход перемещаемой среды'
            size='large'
            disabled
          />
          <InputNumberForm
            name='t'
            addonBefore={<i>t =</i>}
            addonAfter={<i>°C</i>}
            label='Температура перемещаемой среды'
            size='large'
            disabled
            fixValue={1}
          />
          <InputNumberForm
            name='rho'
            addonBefore={<i>ρ =</i>}
            addonAfter={
              <i>
                кг/м<sup>3</sup>
              </i>
            }
            label='Плотность перемещаемой среды'
            disabled
            size='large'
          />
          <InputNumberForm
            name='Lv'
            addonBefore={
              <i>
                L<sub>v</sub> =
              </i>
            }
            addonAfter={
              <i>
                м<sup>3</sup>/ч
              </i>
            }
            label='Объемный расход перемещаемой среды'
            disabled
            size='large'
          />
        </div>

        <div className='grid grid-cols-3 gap-4 gap-y-8 items-end'>
          <InputNumberForm
            name='Pst'
            addonBefore={
              <i>
                P
                <sub>
                  st<sub>tN</sub>
                </sub>
                =
              </i>
            }
            addonAfter={<i>Па</i>}
            disabled
            label='Потери давления при температуре перемещаемой среды'
            fixValue={0}
            size='large'
          />
          <InputNumberForm
            name='Pgrav'
            addonBefore={
              <i>
                ΔP<sub>грав</sub> =
              </i>
            }
            addonAfter={<i>Па</i>}
            label='Гравитационное давление'
            fixValue={0}
            disabled
            size='large'
          />
          <InputNumberForm
            name='Pst_apo'
            addonBefore={
              <i>
                P
                <sub>
                  st<sub>tN+g</sub>
                </sub>
                =
              </i>
            }
            addonAfter={<i>Па</i>}
            label='Потери давления при температуре перемещаемой среды и с учетом гравитационного давления'
            fixValue={0}
            disabled
            size='large'
          />
          <InputNumberForm
            name='Ps'
            addonBefore={
              <i>
                P<sub>st</sub> =
              </i>
            }
            addonAfter={<i>Па</i>}
            label='Приведенное статическое давление вентилятора'
            disabled
            size='large'
          />
          <InputNumberForm
            name='reserve'
            addonBefore={<i>k =</i>}
            addonAfter={<i>%</i>}
            label='Запас'
            onBlur={blurFunction}
            size='large'
            disabled={isObserver}
          />
          <InputNumberForm
            name='Ps_reserve'
            addonBefore={
              <i>
                P<sub>st_k</sub> =
              </i>
            }
            addonAfter={<i>Па</i>}
            label='Приведенное статическое давление вентилятора с запасом'
            disabled
            size='large'
          />
        </div>
        <Title level={5}>Расчет гравитационного давления</Title>

        <GravTable
          handleChange={handlePerformCalculation}
          isObserver={isObserver}
        />

        <div className='flex flex-col gap-4'>
          <div className='flex gap-4'>
            <Checkbox
              checked={Pizb_enabled}
              onChange={handlePizbCheckBox}
              disabled={isObserver}
            />
            <label className='text-secondary'>
              Избыточное давление в защищаемом помещении/объеме (для систем
              приточной противодымной вентиляции)
            </label>
          </div>

          {Pizb_enabled && (
            <div className='grid grid-cols-2'>
              <InputNumberForm
                name='Pizb'
                addonBefore={
                  <i>
                    P<sub>изб</sub> =
                  </i>
                }
                addonAfter={<i>Па</i>}
                label='Избыточное давление в защищаемом помещении/объеме'
                onBlur={blurFunction}
                disabled={isObserver}
                size='large'
              />
            </div>
          )}
        </div>

        <div className='flex flex-col gap-2'>
          <Text type='secondary'>Выберите единицы измерения расхода</Text>
          <div className='w-fit'>
            <RadioGroupForm
              name='mode'
              defaultValue='кг/с'
              className='font-normal'
              options={coverTypeOptions}
              vertical
              onChange={handlePerformCalculation}
              disabled={isObserver}
            />
          </div>
        </div>

        <div className='flex flex-col gap-4'>
          <div className='flex gap-4'>
            <Checkbox
              checked={additional_static_pressure_enabled}
              onChange={handleAdditionalStaticPressureCheckBox}
              disabled={isObserver}
            />
            <label className='text-secondary'>
              Добавочное статическое давление (при естественной компенсации)
            </label>
          </div>

          {additional_static_pressure_enabled && (
            <AdditionalStaticPressureTable
              handleChange={handlePerformCalculation}
              isObserver={isObserver}
            />
          )}

          {additional_static_pressure_enabled && (
            <div className='grid grid-cols-2'>
              <InputNumberForm
                max={150}
                name='sumP0'
                addonBefore={<i>∑P₀ =</i>}
                addonAfter={<i>Па</i>}
                label='Добавочное статическое давление'
                disabled
                size='large'
                fixValue={1}
              />
            </div>
          )}
        </div>

        <Title level={5}>Аэродинамический расчет по участкам</Title>
        <div>
          <DynamicAerodynamicsTable
            handleChange={handlePerformCalculation}
            isObserver={isObserver}
          />
        </div>
      </form>
    </FormProvider>
  );
};
